// Adapted from beautifului.dev (MIT, © 2026 Shane Levine).
//
// Voice input for the companion prompt bar (ORA-600).
//
// A companion-local port of the legacy panel's voice input (deleted in
// ORA-455), with the companion's own analytics surface and consent location.
// It reuses the shared pieces — the browser-speech helpers, the assent
// service, and the shared `AudioConsentModal`.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { Microphone } from '@phosphor-icons/react';
import type { RootState } from '@/store/store';
import { AssentType } from '@/types/Assent';
import { createAssents, getLatestAssents, VERSIONS } from '@/services/assent';
import { capture } from '@/utils/analytics';
import {
  buildSpeechDraft,
  getSpeechRecognitionConstructor,
  normalizeTranscriptText,
  type BrowserSpeechRecognition,
} from '@/utils/chatUtils/chatVoiceUtils';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import Tooltip from '@/components/ui/tooltip';
import AudioConsentModal from '../AudioConsentModal';
import { cn } from '@/lib/utils';

/** Every event from this component names the companion, not the legacy panel. */
const SURFACE = 'companion';

/** The three equaliser bars, tallest in the middle, each on its own delay. */
const EQUALISER_BARS = [
  { heightClass: 'h-2', delayMs: 0 },
  { heightClass: 'h-3.5', delayMs: 150 },
  { heightClass: 'h-2.5', delayMs: 300 },
];

interface ChatVoiceInputProps {
  /** The current draft. Dictation appends to it rather than replacing it. */
  value: string;
  /** Called with the draft plus the transcript so far, on every result. */
  onChange: (next: string) => void;
  /** Streaming, blocked, or out of chats — the same lock the send button has. */
  disabled: boolean;
  /** Put the caret back in the textarea once recording starts. */
  onFocusInput?: () => void;
  /** Recording or transcribing, so the composer can hold the send button. */
  onActiveChange?: (isActive: boolean) => void;
}

/**
 * Dictation for the prompt bar: a mic that becomes a live equaliser while it
 * listens, and nothing at all in a browser that cannot listen.
 *
 * Recording is gated on the audio assent the student may not have given yet,
 * which is why the consent modal lives in here: the button is the only thing
 * that can ask for it, and asking has to happen before the first recording,
 * not after.
 */
const ChatVoiceInput = ({
  value,
  onChange,
  disabled,
  onFocusInput,
  onActiveChange,
}: ChatVoiceInputProps) => {
  const currentUserId = useSelector(
    (state: RootState) => state.user?.profile?.id
  );

  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [hasAudioConsent, setHasAudioConsent] = useState(false);
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const [isSavingConsent, setIsSavingConsent] = useState(false);
  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  const baseDraftRef = useRef('');
  const finalTranscriptRef = useRef('');
  const interimTranscriptRef = useRef('');
  const abortedRef = useRef(false);
  const pendingStartRef = useRef(false);
  // The recogniser's callbacks outlive the render that created them, so they
  // read the draft and the change handler through refs rather than closing
  // over a value that is stale by the second word.
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Resolved once: a browser either has the API or it does not, and a native
  // shell never does (the helper refuses on Capacitor).
  const isSupported = useMemo(
    () => Boolean(getSpeechRecognitionConstructor()),
    []
  );

  const isActive = isRecording || isTranscribing;

  useEffect(() => {
    onActiveChange?.(isActive);
  }, [isActive, onActiveChange]);

  const stopRecognition = useCallback(
    ({ abort = false }: { abort?: boolean } = {}) => {
      const recognition = recognitionRef.current;

      if (!recognition) {
        setIsRecording(false);
        setIsTranscribing(false);
        return;
      }

      setIsRecording(false);
      // Stopping still has a tail — the last phrase is finalised after the
      // call returns — so the button reports transcribing until `onend`.
      setIsTranscribing(!abort);
      abortedRef.current = abort;

      try {
        if (abort) recognition.abort();
        else recognition.stop();
      } catch (error) {
        console.error('Error stopping speech recognition:', error);
        recognitionRef.current = null;
        setIsTranscribing(false);
      }
    },
    []
  );

  useEffect(() => () => stopRecognition({ abort: true }), [stopRecognition]);

  useEffect(() => {
    if (!isSupported) return;

    if (!currentUserId) {
      setHasAudioConsent(false);
      setIsConsentChecked(true);
      setIsConsentModalOpen(false);
      pendingStartRef.current = false;
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const latest = await getLatestAssents(currentUserId);

        const granted =
          latest?.some(
            (assent) =>
              assent.type === AssentType.audio &&
              assent.version === VERSIONS.audio
          ) ?? false;

        if (!cancelled) setHasAudioConsent(granted);
      } catch (error) {
        console.error('Failed to fetch audio assent status:', error);
        if (!cancelled) setHasAudioConsent(false);
      } finally {
        if (!cancelled) setIsConsentChecked(true);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [currentUserId, isSupported]);

  const beginRecording = useCallback(() => {
    const SpeechRecognition = getSpeechRecognitionConstructor();

    if (!SpeechRecognition) {
      enqueueSnackbar('Speech capture is not supported in this browser.', {
        autoHideDuration: 4000,
      });

      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.maxAlternatives = 1;

      baseDraftRef.current = valueRef.current;
      finalTranscriptRef.current = '';
      interimTranscriptRef.current = '';
      abortedRef.current = false;

      recognition.onresult = (event) => {
        let finalChunk = '';
        let interimChunk = '';

        for (
          let index = event.resultIndex;
          index < event.results.length;
          index += 1
        ) {
          const result = event.results[index];
          const transcript = result[0]?.transcript || '';

          if (result.isFinal) {
            finalChunk = normalizeTranscriptText(`${finalChunk} ${transcript}`);
          } else {
            interimChunk = normalizeTranscriptText(
              `${interimChunk} ${transcript}`
            );
          }
        }

        if (finalChunk) {
          finalTranscriptRef.current = normalizeTranscriptText(
            `${finalTranscriptRef.current} ${finalChunk}`
          );
        }

        interimTranscriptRef.current = interimChunk;

        const transcript = normalizeTranscriptText(
          `${finalTranscriptRef.current} ${interimTranscriptRef.current}`
        );

        onChangeRef.current(buildSpeechDraft(baseDraftRef.current, transcript));
      };

      recognition.onerror = (event) => {
        // "aborted" is our own cancel and "no-speech" is a pause, so neither
        // is worth a toast.
        if (event.error === 'aborted' || event.error === 'no-speech') return;

        console.error('Speech recognition error:', event);

        enqueueSnackbar('Speech to text failed. Please try again.', {
          variant: 'error',
          autoHideDuration: 4000,
        });

        capture('chat_voice_transcription_failed', {
          provider: 'web_speech',
          reason: event.error,
          surface: SURFACE,
        });
      };

      recognition.onend = () => {
        const transcript = normalizeTranscriptText(
          `${finalTranscriptRef.current} ${interimTranscriptRef.current}`
        );

        if (!abortedRef.current && transcript) {
          capture('chat_voice_transcription_succeeded', {
            provider: 'web_speech',
            auto_send: false,
            character_count: transcript.length,
            surface: SURFACE,
          });
        }

        recognitionRef.current = null;
        abortedRef.current = false;
        setIsRecording(false);
        setIsTranscribing(false);
      };

      recognitionRef.current = recognition;
      setIsRecording(true);
      setIsTranscribing(false);
      recognition.start();
      onFocusInput?.();

      capture('chat_voice_recording_started', {
        provider: 'web_speech',
        surface: SURFACE,
      });
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      recognitionRef.current = null;
      setIsRecording(false);
      setIsTranscribing(false);

      enqueueSnackbar(
        'Could not start speech to text. Please check your microphone permissions and try again.',
        { variant: 'error', autoHideDuration: 4000 }
      );

      capture('chat_voice_transcription_failed', {
        provider: 'web_speech',
        reason: 'start_exception',
        surface: SURFACE,
      });
    }
  }, [onFocusInput]);

  const acceptConsent = useCallback(async () => {
    if (!currentUserId) {
      enqueueSnackbar('You need to be signed in to use voice input.', {
        variant: 'error',
        autoHideDuration: 3000,
      });

      return;
    }

    const shouldStart = pendingStartRef.current;
    pendingStartRef.current = false;

    setHasAudioConsent(true);
    setIsConsentChecked(true);
    setIsConsentModalOpen(false);

    capture('chat_voice_consent_accepted', { surface: SURFACE });

    // Recording starts on the click that accepted, not on the round trip:
    // the student asked to talk, and the assent is recorded either way.
    if (shouldStart) beginRecording();

    setIsSavingConsent(true);

    try {
      await createAssents(currentUserId, [
        {
          type: AssentType.audio,
          version: VERSIONS.audio,
          dateAccepted: new Date().toISOString(),
          location: 'companion',
        },
      ]);
    } catch (error) {
      console.error('Failed to save audio consent:', error);
      setHasAudioConsent(false);
      if (shouldStart) stopRecognition({ abort: true });

      enqueueSnackbar('Unable to save your audio consent. Please try again.', {
        variant: 'error',
        autoHideDuration: 4000,
      });
    } finally {
      setIsSavingConsent(false);
    }
  }, [beginRecording, currentUserId, stopRecognition]);

  const declineConsent = useCallback(() => {
    pendingStartRef.current = false;
    setIsConsentModalOpen(false);
    capture('chat_voice_consent_declined', { surface: SURFACE });
  }, []);

  const handleClick = useCallback(() => {
    if (disabled) return;

    if (isRecording) {
      stopRecognition();

      capture('chat_voice_recording_stopped', {
        provider: 'web_speech',
        surface: SURFACE,
      });

      return;
    }

    if (isTranscribing) return;

    if (!isConsentChecked) {
      enqueueSnackbar('Checking audio consent. Please try again in a moment.', {
        autoHideDuration: 3000,
      });

      return;
    }

    if (!hasAudioConsent) {
      pendingStartRef.current = true;
      setIsConsentModalOpen(true);
      capture('chat_voice_consent_prompted', { surface: SURFACE });
      return;
    }

    pendingStartRef.current = false;
    beginRecording();
  }, [
    beginRecording,
    disabled,
    hasAudioConsent,
    isConsentChecked,
    isRecording,
    isTranscribing,
    stopRecognition,
  ]);

  if (!isSupported) return null;

  const tooltip = disabled
    ? 'Wait for the response to finish'
    : isRecording
      ? 'Tap to stop recording'
      : isTranscribing
        ? 'Finishing transcription…'
        : 'Speech to text';

  return (
    <>
      <Tooltip title={tooltip} enterTouchDelay={0}>
        <span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={isRecording ? 'Stop recording' : 'Start speech to text'}
            aria-pressed={isRecording}
            disabled={disabled || (isTranscribing && !isRecording)}
            onClick={handleClick}
            className={cn(
              'rounded-full transition-transform active:scale-[.94]',
              // Same touch target as the composer's other buttons: 36px for
              // a pointer, 44px for a finger. Kept literal for the lint rule.
              'size-9 [@media(max-width:560px)]:size-11',
              isRecording && 'bg-surface-highlight text-fg-primary'
            )}
          >
            {isTranscribing && !isRecording ? (
              <Spinner aria-label="Transcribing audio" size={16} />
            ) : isRecording ? (
              <span
                aria-hidden="true"
                className="flex h-3.5 items-end justify-center gap-[2.5px]"
              >
                {EQUALISER_BARS.map((bar) => (
                  <span
                    key={bar.delayMs}
                    // Resting state (reduced motion, tests): the bars stay
                    // visible at full opacity rather than sitting at 0.15.
                    className={cn(
                      'w-[2.5px] rounded-full bg-current opacity-100',
                      'animate-chat-pixel-on',
                      bar.heightClass
                    )}
                    style={{ animationDelay: `${bar.delayMs}ms` }}
                  />
                ))}
              </span>
            ) : (
              <Microphone size={18} weight="fill" aria-hidden="true" />
            )}
          </Button>
        </span>
      </Tooltip>
      <AudioConsentModal
        open={isConsentModalOpen}
        isSaving={isSavingConsent}
        onAccept={() => void acceptConsent()}
        onClose={declineConsent}
      />
    </>
  );
};

export default ChatVoiceInput;
